import { NavigableModal } from '$components'
import { useNavigate, useParams } from 'react-router-dom'
import { RescheduleAppointmentForm } from '$forms/reschedule-appointment/index'
import { RescheduleAppointmentSchema, Input } from '$forms/reschedule-appointment/schema'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { appointmentShowOptions, appointmentShowQueryKey, appointmentRescheduleMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import dayjs from 'dayjs'

export function RescheduleAppointmentModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...appointmentShowOptions({
      path: {
        id: Number(id),
      },
    }),
  })

  const { mutate: rescheduleAppointment, isPending: isUpdatingAppointment } = useMutation({
    ...appointmentRescheduleMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appointmentShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })
      ToastHelper.success('Appointment rescheduled successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to reschedule appointment.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: RescheduleAppointmentSchema) => {
    rescheduleAppointment({
      path: {
        id: Number(id),
      },
      body: {
        start_at: values[Input.Slot].start_at,
        finish_at: values[Input.Slot].finish_at,
        groomer_id: Number(values[Input.Slot].groomer.id),
      },
    })
  }

  const appointment = data?.data

  if (!appointment) {
    return null
  }

  return (
    <RescheduleAppointmentForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingAppointment}
      defaultValues={{
        [Input.Groomer]: {
          id: appointment.groomer.id,
          first_name: appointment.groomer.first_name,
          last_name: appointment.groomer.last_name,
        },
        [Input.AppointmentId]: appointment.id,
        [Input.StartAt]: appointment.start_at,
        [Input.FinishAt]: appointment.finish_at,
        [Input.Duration]: dayjs(appointment.finish_at).diff(dayjs(appointment.start_at), 'minute'),
        [Input.Price]: appointment.price_in_cents,
        [Input.Date]: dayjs(appointment.start_at).isBefore(dayjs()) ? dayjs().toISOString() : appointment.start_at,
        [Input.Services]: appointment.services.map(service => ({
          id: service.service.id,
          value: service.service_values.name,
        })),
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Reschedule Appointment
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <RescheduleAppointmentForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <RescheduleAppointmentForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </RescheduleAppointmentForm>
  )
}
